/**
 * Admin token usage API service.
 *
 * Typed client for the admin-only token usage aggregation endpoints backing the
 * token usage dashboard (summary, by-user, by-task, and per-user task detail).
 *
 * @module tokenUsageApi
 */

import { apiGet } from './apiUtils';

/** Filters accepted by the token usage endpoints. */
export interface TokenUsageFilters {
  startDate?: string;
  endDate?: string;
  userId?: string;
  task?: string;
  feature?: string;
  provider?: string;
}

/** Effective filter range echoed back by every endpoint. */
export interface TokenUsageRange {
  startDate?: string | null;
  endDate?: string | null;
  userId?: string | null;
  task?: string | null;
  feature?: string | null;
  provider?: string | null;
}

export interface TokenUsageSummary {
  totalTokens: number;
  promptTokens: number;
  completionTokens: number;
  eventCount: number;
  firstEventAt: string | null;
  lastEventAt: string | null;
  range: TokenUsageRange;
}

export interface TokenUsageUserRow {
  userId: string | null;
  userLabel: string;
  totalTokens: number;
  promptTokens: number;
  completionTokens: number;
  eventCount: number;
  lastUsedAt: string | null;
}

export interface TokenUsageByUser {
  users: TokenUsageUserRow[];
  range: TokenUsageRange;
}

export interface TokenUsageTaskRow {
  task: string;
  feature: string;
  model: string | null;
  totalTokens: number;
  promptTokens: number;
  completionTokens: number;
  eventCount: number;
  lastUsedAt: string | null;
  audioSeconds: number | null;
}

export interface TokenUsageByTask {
  tasks: TokenUsageTaskRow[];
  range: TokenUsageRange;
}

export interface TokenUsageUserDetail {
  userId: string | null;
  userLabel: string;
  tasks: TokenUsageTaskRow[];
  range: TokenUsageRange;
}

const buildQuery = (filters: TokenUsageFilters = {}): string => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '' && value !== 'all') {
      params.append(key, String(value));
    }
  });
  const query = params.toString();
  return query ? `?${query}` : '';
};

const fetchOrThrow = async <T>(url: string, context: string, errorMessage: string): Promise<T> => {
  const result = await apiGet(url, { errorMessage, context, throwOnError: true });
  if (result === null) {
    throw new Error(errorMessage);
  }
  return result as T;
};

/** Fetch aggregate token usage totals. */
export const getTokenUsageSummary = (
  filters: TokenUsageFilters = {},
): Promise<TokenUsageSummary> =>
  fetchOrThrow<TokenUsageSummary>(
    `/admin/token-usage/summary${buildQuery(filters)}`,
    'getTokenUsageSummary',
    'Failed to load token usage summary',
  );

/** Fetch token usage grouped by user. */
export const getTokenUsageByUser = (
  filters: TokenUsageFilters = {},
): Promise<TokenUsageByUser> =>
  fetchOrThrow<TokenUsageByUser>(
    `/admin/token-usage/by-user${buildQuery(filters)}`,
    'getTokenUsageByUser',
    'Failed to load token usage by user',
  );

/** Fetch token usage grouped by task/feature. */
export const getTokenUsageByTask = (
  filters: TokenUsageFilters = {},
): Promise<TokenUsageByTask> =>
  fetchOrThrow<TokenUsageByTask>(
    `/admin/token-usage/by-task${buildQuery(filters)}`,
    'getTokenUsageByTask',
    'Failed to load token usage by task',
  );

/** Fetch per-task/feature token usage for a single user. */
export const getTokenUsageUserDetail = (
  userId: string,
  filters: TokenUsageFilters = {},
): Promise<TokenUsageUserDetail> =>
  fetchOrThrow<TokenUsageUserDetail>(
    `/admin/token-usage/users/${encodeURIComponent(userId)}/tasks${buildQuery(filters)}`,
    'getTokenUsageUserDetail',
    'Failed to load user token usage detail',
  );
